import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Hammer } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/rebatesData';

const STORE_BADGE = {
  Menards: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  Lowes: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  'Home Depot': 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  Other: 'bg-[hsl(var(--muted))] text-foreground border-border',
};

export const RebatesByStoreCard = ({ stats, onPick }) => {
  const { t } = useTranslation();
  const rows = stats?.byStore || [];
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="rebates-by-store"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-[hsl(var(--primary))]" />
            <h2 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('rebateTracker.byStore.title')}
            </h2>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('rebateTracker.byStore.count', { count: rows.length })}
          </span>
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            {t('rebateTracker.byStore.empty')}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <button
                key={r.store}
                type="button"
                onClick={() => onPick?.(r.store)}
                className={cn(
                  'w-full text-left rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2 hover:border-[hsl(var(--primary))]/30 transition-colors'
                )}
                data-testid={`rebates-store-row-${r.store.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn('uppercase text-[10px] tracking-[0.16em]', STORE_BADGE[r.store] || STORE_BADGE.Other)}
                    >
                      {r.store}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground">
                      {t('rebateTracker.byStore.rateLine', {
                        pct: Math.round((r.rate || 0) * 100),
                        count: r.count,
                      })}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-[hsl(var(--success))] tabular-nums">
                    {formatUsd(r.total)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const RebatesByJobCard = ({ stats, onPick }) => {
  const { t } = useTranslation();
  const rows = (stats?.byJob || []).slice(0, 8);
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="rebates-by-job"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Hammer size={14} className="text-[hsl(var(--primary))]" />
            <h2 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('rebateTracker.byJob.title')}
            </h2>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('rebateTracker.byJob.count', { count: (stats?.byJob || []).length })}
          </span>
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            {t('rebateTracker.byJob.empty')}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <button
                key={r.job_id}
                type="button"
                onClick={() => onPick?.(r.job_id)}
                className={cn(
                  'w-full text-left rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2 hover:border-[hsl(var(--primary))]/30 transition-colors'
                )}
                data-testid={`rebates-job-row-${r.job_id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-foreground truncate">
                      {r.job_name}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {r.client || t('rebateTracker.byJob.noClient')} {'\u00b7'}{' '}
                      {t('rebateTracker.byJob.receipts', { count: r.count })}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-[hsl(var(--success))] tabular-nums shrink-0">
                    {formatUsd(r.total)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RebatesByStoreCard;
